import React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';

// Simple 3-background system: forest (default), city, beach
const VALID_BACKGROUNDS = ['forest', 'city', 'beach'];
const TTS_VOICE_OPTIONS = [
  { id: '9MQbSvZCz1PCthbJxqlc', labelKey: 'settings.voiceChildishFemale', lang: 'lt' },
  { id: 'TpCiT74zy2gtqQN6PG8l', labelKey: 'settings.voiceAdultMale', lang: 'lt' },
  { id: 'MeRxhSiWwl3IlEmu3Ui4', labelKey: 'settings.voiceChildishFemale', lang: 'en' },
  { id: 's2HJxLa50F7HaCIIdNPh', labelKey: 'settings.voiceAdultMale', lang: 'en' },
];
const DEFAULT_TTS_VOICE_ID = TTS_VOICE_OPTIONS[0].id;

const normalizeBackground = (storedValue) => {
  if (!storedValue || !VALID_BACKGROUNDS.includes(storedValue)) {
    return 'forest'; // Default to forest
  }
  return storedValue;
};

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const [showImages, setShowImages] = useState(() => JSON.parse(localStorage.getItem('ttt-show-images') || 'true'));
  const [openDyslexicEnabled, setOpenDyslexicEnabled] = useState(() => {
    const stored = localStorage.getItem('ttt-open-dyslexic-enabled');
    if (stored !== null) {
      return JSON.parse(stored);
    }
    const legacyStored = localStorage.getItem('ttt-dyslexic-font-enabled');
    if (legacyStored !== null) {
      return JSON.parse(legacyStored);
    }
    return JSON.parse(localStorage.getItem('ttt-dyslexia-mode') || 'false');
  });
  const [fontSize, setFontSize] = useState(() => localStorage.getItem('ttt-font-size') || 'medium');
  const [backgroundMode, setBackgroundMode] = useState(() => normalizeBackground(localStorage.getItem('ttt-background-mode')));
  const [userProfile, setUserProfile] = useState(() => JSON.parse(localStorage.getItem('ttt-user-profile') || 'null'));
  const [rainSoundEnabled, setRainSoundEnabled] = useState(() => JSON.parse(localStorage.getItem('ttt-rain-sound-enabled') || 'false'));
  const [rainVolume, setRainVolume] = useState(() => JSON.parse(localStorage.getItem('ttt-rain-volume') || '50'));
  const [rainBgEnabled, setRainBgEnabled] = useState(() => JSON.parse(localStorage.getItem('ttt-rain-bg-enabled') || 'false'));
  const [detailMode, setDetailMode] = useState(() => JSON.parse(localStorage.getItem('ttt-detail-mode') || 'false'));
  const [userProgress, setUserProgress] = useState(() => JSON.parse(localStorage.getItem('ttt-user-progress') || '{}'));
  const [unlockableThemes, setUnlockableThemes] = useState(() => JSON.parse(localStorage.getItem('ttt-unlocked-themes') || '[]'));
  const [chimeEnabled, setChimeEnabled] = useState(() => JSON.parse(localStorage.getItem('ttt-chime-enabled') || 'true'));
  // TTS provider is locked to ElevenLabs; user can only pick allowlisted voice IDs.
  const [ttsModel, setTtsModel] = useState('elevenlabs');
  const [ttsVoiceId, setTtsVoiceId] = useState(() => {
    const stored = localStorage.getItem('ttt-tts-voice-id');
    const isAllowed = TTS_VOICE_OPTIONS.some(voice => voice.id === stored);
    return isAllowed ? stored : DEFAULT_TTS_VOICE_ID;
  });
  const [themeIconsEnabled, setThemeIconsEnabled] = useState(() => JSON.parse(localStorage.getItem('ttt-theme-icons-enabled') || 'false'));
  const [lightMode, setLightMode] = useState(() => JSON.parse(localStorage.getItem('ttt-light-mode') || 'false'));
  // Onboarding complete flag - default to true for existing users (those who have any other TTT settings)
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    const stored = localStorage.getItem('ttt-onboarding-complete');
    if (stored !== null) return JSON.parse(stored);
    // For existing users who have other settings, skip onboarding
    const hasExistingSettings = localStorage.getItem('ttt-user-progress') || 
                                 localStorage.getItem('ttt-background-mode') ||
                                 localStorage.getItem('ttt-language');
    return hasExistingSettings ? true : false;
  });
  // Reduced motion preference
  const [reducedMotion, setReducedMotion] = useState(() => {
    const stored = localStorage.getItem('ttt-reduced-motion');
    if (stored !== null) return JSON.parse(stored);
    // Default to system preference
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
  });
  // Party mode for mobile and desktop
  // Phones are the primary device, so the celebration defaults to on there.
  // Only an untouched key takes the default: a stored 'false' stays off.
  const [partyModePhone, setPartyModePhone] = useState(() => {
    const stored = localStorage.getItem('ttt-party-mode-phone');
    if (stored !== null && stored !== '') return JSON.parse(stored);
    return true;
  });
  const [partyModeDesktop, setPartyModeDesktop] = useState(() => JSON.parse(localStorage.getItem('ttt-party-mode-desktop') || 'true'));
  // Global trophy count - single source of truth across all situations
  const [totalTrophies, setTotalTrophies] = useState(() => {
    const stored = localStorage.getItem('ttt_totalTrophies');
    if (stored !== null) {
      return parseInt(stored, 10) || 0;
    }
    // Migration: if no global trophies exist, try to sum from old per-situation trophies
    const oldProgress = JSON.parse(localStorage.getItem('ttt-user-progress') || '{}');
    const oldTotal = Object.values(oldProgress).reduce((acc, curr) => acc + (curr.trophies || 0), 0);
    return oldTotal;
  });

  useEffect(() => localStorage.setItem('ttt-show-images', JSON.stringify(showImages)), [showImages]);
  useEffect(() => {
    const value = JSON.stringify(openDyslexicEnabled);
    localStorage.setItem('ttt-open-dyslexic-enabled', value);
    localStorage.setItem('ttt-dyslexic-font-enabled', value);
    localStorage.setItem('ttt-dyslexia-mode', value);
  }, [openDyslexicEnabled]);
  useEffect(() => localStorage.setItem('ttt-font-size', fontSize), [fontSize]);
  useEffect(() => localStorage.setItem('ttt-background-mode', backgroundMode), [backgroundMode]);
  useEffect(() => localStorage.setItem('ttt-user-profile', JSON.stringify(userProfile)), [userProfile]);
  useEffect(() => localStorage.setItem('ttt-rain-sound-enabled', JSON.stringify(rainSoundEnabled)), [rainSoundEnabled]);
  useEffect(() => localStorage.setItem('ttt-rain-volume', JSON.stringify(rainVolume)), [rainVolume]);
  useEffect(() => localStorage.setItem('ttt-rain-bg-enabled', JSON.stringify(rainBgEnabled)), [rainBgEnabled]);
  useEffect(() => localStorage.setItem('ttt-detail-mode', JSON.stringify(detailMode)), [detailMode]);
  useEffect(() => localStorage.setItem('ttt-user-progress', JSON.stringify(userProgress)), [userProgress]);
  useEffect(() => localStorage.setItem('ttt-unlocked-themes', JSON.stringify(unlockableThemes)), [unlockableThemes]);
  useEffect(() => localStorage.setItem('ttt-chime-enabled', JSON.stringify(chimeEnabled)), [chimeEnabled]);
  useEffect(() => localStorage.setItem('ttt-tts-model', ttsModel), [ttsModel]);
  // Persist selected ElevenLabs voice ID.
  useEffect(() => localStorage.setItem('ttt-tts-voice-id', ttsVoiceId), [ttsVoiceId]);
  useEffect(() => localStorage.setItem('ttt-theme-icons-enabled', JSON.stringify(themeIconsEnabled)), [themeIconsEnabled]);
  useEffect(() => localStorage.setItem('ttt-light-mode', JSON.stringify(lightMode)), [lightMode]);
  // Persist onboarding and reduced motion
  useEffect(() => localStorage.setItem('ttt-onboarding-complete', JSON.stringify(onboardingComplete)), [onboardingComplete]);
  useEffect(() => localStorage.setItem('ttt-reduced-motion', JSON.stringify(reducedMotion)), [reducedMotion]);
  // Persist global trophy count to localStorage
  useEffect(() => localStorage.setItem('ttt_totalTrophies', totalTrophies.toString()), [totalTrophies]);
  // Persist party mode settings
  useEffect(() => localStorage.setItem('ttt-party-mode-phone', JSON.stringify(partyModePhone)), [partyModePhone]);
  useEffect(() => localStorage.setItem('ttt-party-mode-desktop', JSON.stringify(partyModeDesktop)), [partyModeDesktop]);

  const toggleRainSound = () => setRainSoundEnabled(prev => !prev);
  const toggleRainBg = () => setRainBgEnabled(prev => !prev);
  const toggleOpenDyslexic = (nextValue) =>
    setOpenDyslexicEnabled(prev => (typeof nextValue === 'boolean' ? nextValue : !prev));

  const getSituationProgress = (situationId) => {
    // Returns per-situation progress including trophies count
    // Trophies are updated via awardTrophiesForSituation() which updates both global and per-situation counts
    return userProgress[situationId] || { successfulInteractions: 0, trophies: 0 };
  };
  
  // Get global trophy count (single source of truth)
  const getTotalTrophies = () => {
    return totalTrophies;
  };

  // Add trophies to the global count
  // This is the primary way to award trophies after chat completion
  const addTrophies = (amount) => {
    setTotalTrophies(prev => {
      const newTotal = prev + amount;
      return newTotal;
    });
  };

  // Award trophies for a specific situation (updates both global and per-situation counts)
  // If situationId is null, only updates global trophies (e.g., for Unknown Situation)
  const awardTrophiesForSituation = (situationId, amount) => {
    // Always update global total
    addTrophies(amount);

    // If situationId is provided, also update that situation's progress
    if (!situationId) return;

    setUserProgress(prev => {
      const current = prev[situationId] || { successfulInteractions: 0, trophies: 0 };
      return {
        ...prev,
        [situationId]: {
          ...current,
          trophies: (current.trophies || 0) + amount,
        },
      };
    });
  };

  // Legacy function - kept for backward compatibility but no longer used for trophies
  const addTrophy = (situationId) => {
    // Deprecated: use addTrophies() instead for global trophy system
    console.warn('addTrophy is deprecated. Use addTrophies() for global trophy system.');
  };

  // Complete a situation - tracks successful interactions, but NOT trophies
  // Trophies are now awarded separately via awardTrophiesForSituation() in completion screens
  const completeSituation = (situationId) => {
    setUserProgress(prev => {
      const current = prev[situationId] || { successfulInteractions: 0, trophies: 0 };
      return {
        ...prev,
        [situationId]: { ...current, successfulInteractions: current.successfulInteractions + 1 }
      };
    });
    
    // Track activity for GitHub-style tracker
    trackActivity(situationId, true);
  };

  // Track activity for the GitHub-style activity tracker
  const trackActivity = (situationId, success = true) => {
    const historyKey = 'situationActivityHistory';
    try {
      const stored = localStorage.getItem(historyKey);
      const history = stored ? JSON.parse(stored) : [];
      
      // Add new activity entry
      history.push({
        date: new Date().toISOString(),
        situationId,
        success
      });
      
      // Keep only last 365 days of data
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const filtered = history.filter(entry => new Date(entry.date) > oneYearAgo);
      
      localStorage.setItem(historyKey, JSON.stringify(filtered));
    } catch (e) {
      console.error('Failed to track activity:', e);
    }
  };

  const resetToDefaults = () => {
    setShowImages(true);
    setOpenDyslexicEnabled(false);
    setFontSize('medium');
    setBackgroundMode('mono');
    setRainSoundEnabled(false);
    setRainVolume(50);
    setRainBgEnabled(false);
    setDetailMode(false);
    setChimeEnabled(true);
    setTtsModel('elevenlabs');
    setTtsVoiceId(DEFAULT_TTS_VOICE_ID);
    setThemeIconsEnabled(false);
  };

  const value = {
    showImages, setShowImages,
    dyslexiaMode: openDyslexicEnabled,
    setDyslexiaMode: (value) => toggleOpenDyslexic(Boolean(value)),
    fontSize, setFontSize,
    openDyslexicEnabled,
    setOpenDyslexicEnabled,
    toggleOpenDyslexic,
    backgroundMode, setBackgroundMode,
    userProfile, setUserProfile,
    rainSoundEnabled, setRainSoundEnabled, toggleRainSound,
    rainVolume, setRainVolume,
    rainBgEnabled, setRainBgEnabled, toggleRainBg,
    detailMode, setDetailMode,
    userProgress,
    getSituationProgress,
    completeSituation,
    resetToDefaults,
    getTotalTrophies,
    totalTrophies, // Expose totalTrophies directly for components that need it
    addTrophies, // Primary function for awarding trophies (global only)
    awardTrophiesForSituation, // Unified function for awarding trophies (global + per-situation)
    addTrophy, // Legacy function, kept for backward compatibility
    unlockableThemes,
    setUnlockableThemes,
    chimeEnabled,
    setChimeEnabled,
    ttsModel,
    setTtsModel,
    ttsVoiceId,
    setTtsVoiceId,
    ttsVoiceOptions: TTS_VOICE_OPTIONS,
    themeIconsEnabled,
    setThemeIconsEnabled,
    lightMode,
    setLightMode,
    // Onboarding
    onboardingComplete,
    setOnboardingComplete,
    // Reduced motion
    reducedMotion,
    setReducedMotion,
    // Party mode settings
    partyModePhone,
    setPartyModePhone,
    partyModeDesktop,
    setPartyModeDesktop,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};